// Continue-Listening persistence layer.
//
// Strategy:
// - Always write to localStorage so guests get the feature too.
// - When a user is signed in, ALSO PUT to the backend so the state is portable
//   across devices. Reads prefer the backend response (it's the source of
//   truth) and fall back to localStorage.
// - Track shape persisted:
//     { trackId, stationId, stationName, mode, positionSeconds, updatedAt,
//       trackSnapshot: { id, title, artist, has_cover, media_type } }
//   The snapshot lets the guest card render before any network call.
//
// Privacy: the localStorage entry is plain — fine for an opt-in feature. We
// keep the snapshot small (no streaming URLs / no PII).

import { api } from "./api";

const LS_KEY = "yahweh_continue_listening_v1";
const MIN_SAVE_POSITION_SECONDS = 5; // don't bother saving the first few seconds

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.trackId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocal(state) {
  try {
    if (!state) {
      localStorage.removeItem(LS_KEY);
      return;
    }
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* storage may be full / disabled — non-fatal */
  }
}

function trackSnapshot(track) {
  if (!track) return null;
  return {
    id: track.id,
    title: track.title,
    artist: track.artist,
    has_cover: !!track.has_cover,
    media_type: track.media_type || "audio",
  };
}

export function saveContinueState({
  track,
  stationId,
  stationName,
  mode,
  positionSeconds,
  isAuthed,
}) {
  if (!track || !track.id) return;
  if (!Number.isFinite(positionSeconds) || positionSeconds < MIN_SAVE_POSITION_SECONDS) return;

  const authed = typeof isAuthed === "boolean" ? isAuthed : !!localStorage.getItem("yahweh_token");

  const local = {
    trackId: track.id,
    stationId: stationId || null,
    stationName: stationName || null,
    mode: mode || "normal",
    positionSeconds: Math.max(0, Math.floor(positionSeconds)),
    updatedAt: new Date().toISOString(),
    trackSnapshot: trackSnapshot(track),
  };
  writeLocal(local);

  if (authed) {
    // Fire-and-forget — do not block playback.
    api.put("/me/playback-state", {
      track_id: track.id,
      station_id: stationId || null,
      station_name: stationName || null,
      mode: mode || "normal",
      position_seconds: local.positionSeconds,
    }).catch(() => {});
  }
}

export async function loadContinueState({ isAuthed } = {}) {
  const authed = typeof isAuthed === "boolean" ? isAuthed : !!localStorage.getItem("yahweh_token");
  if (authed) {
    try {
      const { data } = await api.get("/me/playback-state");
      const remote = data?.state || null;
      if (remote && remote.track) {
        // Mirror to localStorage so the guest fallback is also fresh.
        writeLocal({
          trackId: remote.track.id,
          stationId: remote.station_id || null,
          stationName: remote.station_name || null,
          mode: remote.mode || "normal",
          positionSeconds: Math.max(0, Math.floor(remote.position_seconds || 0)),
          updatedAt: remote.updated_at,
          trackSnapshot: trackSnapshot(remote.track),
        });
        return { ...remote, source: "remote" };
      }
      // Authed but nothing saved server-side: don't fall back to local — local
      // could be stale from a different account.
      writeLocal(null);
      return null;
    } catch {
      // Network / auth blip — degrade to local snapshot.
    }
  }
  const local = readLocal();
  if (!local) return null;
  return {
    track: local.trackSnapshot ? { ...local.trackSnapshot } : { id: local.trackId, title: "Unknown track", artist: "" },
    station_id: local.stationId,
    station_name: local.stationName,
    mode: local.mode || "normal",
    position_seconds: local.positionSeconds || 0,
    updated_at: local.updatedAt,
    source: "local",
  };
}

export async function clearContinueState({ isAuthed } = {}) {
  const authed = typeof isAuthed === "boolean" ? isAuthed : !!localStorage.getItem("yahweh_token");
  writeLocal(null);
  if (authed) {
    try { await api.delete("/me/playback-state"); } catch { /* swallow */ }
  }
}
